import type { AppData } from "../../types";
import { activeCalls, isLiveCallStatus } from "../../live_calls";
import { el, statusLabel } from "../atoms/dom";
import { statCard } from "../molecules/stat_card";

export function dashboardPage(
  data: AppData,
  onOpenAppointment: (appointmentId: string) => void,
  onOpenConversation: (conversationId: string) => void,
) {
  const branchId = data._meta.selectedContext.branchId;
  const metrics = data.dashboardByBranch[branchId] ?? {
    answeredCalls: 0,
    confirmedAppointments: 0,
    pendingAppointments: 0,
    handoffsWaiting: 0,
  };
  const today = isoDay(new Date());
  const appointments = data.appointments
    .filter((item) => isoDay(new Date(item.startsAt)) === today)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const liveCalls = activeCalls(data);
  const recentCalls = data.conversations
    .filter((call) => !isLiveCallStatus(call.status))
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
    .slice(0, 5);

  return el(
    "div",
    { className: "page-stack" },
    liveCalls.length > 0
      ? el(
          "section",
          { className: "live-overview" },
          el(
            "header",
            { className: "live-overview__header" },
            el("h2", { text: "Aktif görüşmeler" }),
            el(
              "span",
              { className: "live-badge" },
              el("i", { attrs: { "aria-hidden": "true" } }),
              el("b", { text: "Canlı görüşme" }),
            ),
          ),
          el(
            "div",
            { className: "live-overview__grid" },
            ...liveCalls.map((call) =>
              liveCallCard(data, call, () => onOpenConversation(call.id))
            ),
          ),
        )
      : null,
    el(
      "section",
      { className: "stats-grid" },
      statCard("Yanıtlanan arama", String(metrics.answeredCalls), "blue"),
      statCard("Onaylı randevu", String(metrics.confirmedAppointments), "cyan"),
      statCard("Bekleyen randevu", String(metrics.pendingAppointments), "amber"),
      statCard("Destek bekleyen", String(metrics.handoffsWaiting), "violet"),
    ),
    el(
      "section",
      { className: "dashboard-grid" },
      panel(
        "Bugünün randevuları",
        appointments.length === 0
          ? empty("Bugün için randevu bulunmuyor.")
          : el(
              "div",
              { className: "list" },
              ...appointments.map((appointment) => {
                const customer = data.customers.find((item) => item.id === appointment.customerId);
                const service = data.services.find((item) => item.id === appointment.serviceId);
                const row = el(
                  "article",
                  {
                    className: "list-row list-row--interactive",
                    attrs: { role: "button", tabindex: "0" },
                    onClick: () => onOpenAppointment(appointment.id),
                  },
                  el("time", { className: "time-chip", text: timeText(appointment.startsAt) }),
                  el(
                    "div",
                    { className: "list-row__body" },
                    el("strong", { text: customer?.displayName ?? "Anonim müşteri" }),
                    el("span", { text: service?.name ?? "Hizmet" }),
                  ),
                  statusLabel(appointment.status),
                );
                row.addEventListener("keydown", (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpenAppointment(appointment.id);
                  }
                });
                return row;
              }),
            ),
      ),
      panel(
        "Son görüşmeler",
        recentCalls.length === 0
          ? empty("Henüz telefon görüşmesi bulunmuyor.")
          : el(
              "div",
              { className: "list" },
              ...recentCalls.map((call) =>
                el(
                  "article",
                  { className: "list-row" },
                  el("span", { className: "call-avatar", text: initials(call.callerDisplayName) }),
                  el(
                    "div",
                    { className: "list-row__body" },
                    el("strong", { text: call.callerDisplayName }),
                    el("span", { text: `${call.callerPhoneNumber} · ${duration(call.durationSeconds)}` }),
                  ),
                  el("time", { text: relativeDate(call.startedAt) }),
                ),
              ),
            ),
      ),
    ),
  );
}

function liveCallCard(
  data: AppData,
  call: AppData["conversations"][number],
  onOpen: () => void,
) {
  const messages = (data.conversationMessages[call.id] ?? []).slice(-4);
  const card = el(
    "button",
    {
      className: "live-call-card",
      attrs: {
        type: "button",
        "aria-label": `${call.callerDisplayName} canlı görüşmesini aç`,
      },
      onClick: onOpen,
    },
    el(
      "div",
      { className: "live-call-card__identity" },
      el("span", { className: "call-avatar", text: initials(call.callerDisplayName) }),
      el(
        "div",
        {},
        el("strong", { text: call.callerDisplayName }),
        el("span", { text: call.callerPhoneNumber }),
      ),
      el("time", { text: timeText(call.startedAt) }),
    ),
    messages.length === 0
      ? el("p", {
          className: "live-call-card__waiting",
          text: "Konuşma başladığında döküm burada anlık görünecek.",
        })
      : el(
          "div",
          { className: "live-call-card__messages" },
          ...messages.map((message) =>
            el(
              "p",
              {},
              el("b", {
                text: message.role === "assistant"
                  ? "Yapay zekâ: "
                  : `${call.callerDisplayName}: `,
              }),
              document.createTextNode(message.text),
            )
          ),
        ),
    el("span", { className: "live-call-card__action", text: "Görüşmeyi aç →" }),
  );
  return card;
}

export function panel(title: string, content: HTMLElement, action?: HTMLElement) {
  return el(
    "section",
    { className: "panel" },
    el("header", { className: "panel__header" }, el("h2", { text: title }), action),
    content,
  );
}

export function empty(text: string) {
  return el("div", { className: "empty" }, el("p", { text }));
}

export function timeText(value: string) {
  return new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

export function dateText(value: Date) {
  return new Intl.DateTimeFormat("tr-TR", { weekday: "long", day: "numeric", month: "long" }).format(value);
}

export function isoDay(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

export function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toLocaleUpperCase("tr");
}

export function duration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export function relativeDate(value: string) {
  return new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}
